import { ChangeEvent, useMemo, useState } from 'react';

import {
  FilterFormProps,
  FilterOption,
  isDynamicFilter,
  SingleFilterProps,
} from '@/models/FilterConfig';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { FilterPopup } from './FilterPopup';
import { BaseFilters } from '@/models/Service';
import TextInput from '@/components/ui/custom/dataInput/TextInput';
import useDebounce from '@/data/hook/useDebounce';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { LoadingIcon } from '../icons';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { useTranslation } from 'react-i18next';
import { Model as BaseModel } from '@/models/Model';
import SelectFilterHeader from './SelectFilterHeader';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import InfinityList from '@/components/ui/custom/InfinityList';
import { twMerge } from 'tailwind-merge';

export default function SingleFilter<
  Model extends BaseModel,
  FieldValue,
  Filter extends BaseFilters = BaseFilters,
>(
  props: SingleFilterProps<Model, FieldValue, Filter> &
    FilterFormProps & {
      className?: ClassNameComponent;
    },
) {
  const { label, mode, setValue, name, service, withSearch, className } = props;
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { debounce } = useDebounce();

  const isDynamic = isDynamicFilter(props);
  const filters: Filter = (isDynamic ? props.filters || {} : {}) as Filter;

  const { queryFn, queryKey } =
    service?.({ search: debouncedSearch, ...filters })._ctx.infinity || {};

  const {
    results,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isInitialLoading,
    isPreviousData: isLoading,
  } = useInfiniteService<Model>({
    keepPreviousData: true,
    enabled: isDynamic,
    queryFn,
    queryKey,
  });

  const options = useMemo<FilterOption<FieldValue>[]>(
    () => (mode === 'static' ? props.options : props.makeOptions(results)),
    [mode, props, results],
  );

  const handleChange = (value: FieldValue) => {
    setValue(name, value as string | number);
  };

  function onSearch(e: ChangeEvent<HTMLInputElement>) {
    if (!isDynamic) return;
    if (!withSearch) return;
    const value = e.target.value;
    setSearchValue(value);
    debounce(() => setDebouncedSearch(value));
  }

  return (
    <FilterPopup
      className={{ popup: className?.popup }}
      reference={
        <SelectFilterHeader
          selected={label}
          className={{
            ...className,
            base: className?.reference,
            label: className?.label,
          }}
        />
      }
    >
      <ConditionalRenderer condition={isDynamic && props.withSearch}>
        <TextInput
          className={{ input: 'w-full' }}
          value={searchValue}
          placeholder={t('research')}
          icon={isLoading ? <LoadingIcon /> : <MagnifyingGlassIcon />}
          onChange={onSearch}
        />
      </ConditionalRenderer>
      <InfinityList
        onReachEnd={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        scroll
        className={className?.ul}
      >
        <ConditionalRenderer
          condition={!isInitialLoading}
          fallback={<LoadingIcon className="w-4 text-primary self-center" />}
        >
          {options.map(option => (
            <li
              className={twMerge(
                'text-14 font-500 text-base-content hover:text-primary transition-all duration-300 ease-in-out',
                className?.li,
              )}
              key={String(option.value)}
              role="button"
              onClick={() => handleChange(option.value)}
            >
              {option.label}
            </li>
          ))}
        </ConditionalRenderer>
      </InfinityList>
    </FilterPopup>
  );
}
